import { styled } from "goober";
import { useEffect, useMemo } from "react";
import { useVolumeDirectory } from "../../../hooks/useVolumeDirectory";
import { Icon } from "../../Icon";
import { Text } from "../../Text";
import { WavyLine } from "../../WavyLine";
import { type ColumnItem, FileBrowserColumn } from "../FileBrowserColumn";

type Props = {
  pathBase: string;
  relativePath: string;
  heading: string;
  selectedId: string | null;
  focusedColumn: boolean;
  focusedIndex: number;
  columnIndex: number;
  onSelect: (
    id: string,
    label: string,
    rowIndex: number,
    meta?: Record<string, string>
  ) => void;
  onReplace?: (
    id: string,
    label: string,
    meta?: Record<string, string>
  ) => void;
  onRegisterCount: (columnIndex: number, count: number) => void;
};

const StateWrap = styled("div")`
  display: flex;
  flex-direction: column;
  width: 228px;
  height: 100%;
  flex-shrink: 0;
  border-right: 0.5px solid var(--border-alpha);
  padding: 24px 16px;
  gap: 8px;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const IconWrap = styled("div")`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--alpha4);
  border-radius: 12px;
  color: var(--text-secondary);
`;

export function DirectoryColumn({
  pathBase,
  relativePath,
  heading,
  selectedId,
  focusedColumn,
  focusedIndex,
  columnIndex,
  onSelect,
  onReplace,
  onRegisterCount,
}: Props) {
  const { entries, loading, error } = useVolumeDirectory(
    pathBase,
    relativePath
  );

  const effectiveCount = !loading && !error ? entries.length : 0;
  useEffect(() => {
    onRegisterCount(columnIndex, effectiveCount);
  }, [columnIndex, effectiveCount, onRegisterCount]);

  const items: ColumnItem[] = useMemo(
    () =>
      entries.map((e) => ({
        id: e.name,
        label: e.name,
        icon:
          e.kind === "directory"
            ? ("folder" as const)
            : ("file-braces-corner" as const),
        drillable: e.kind === "directory",
      })),
    [entries]
  );

  const withEntryMeta =
    (
      fn:
        | ((
            id: string,
            label: string,
            rowIndex: number,
            meta?: Record<string, string>
          ) => void)
        | undefined
    ) =>
    (id: string, label: string, rowIndex: number) => {
      if (!fn) return;
      const entry = entries.find((e) => e.name === id);
      if (!entry) return;
      fn(id, label, rowIndex, {
        kind: entry.kind,
        size: String(entry.size),
        modifiedAt: String(entry.modifiedAt),
        relativePath: relativePath ? `${relativePath}/${id}` : id,
      });
    };

  const withEntryReplaceMeta =
    (
      fn:
        | ((id: string, label: string, meta?: Record<string, string>) => void)
        | undefined
    ) =>
    (id: string, label: string) => {
      if (!fn) return;
      const entry = entries.find((e) => e.name === id);
      if (!entry) return;
      fn(id, label, {
        kind: entry.kind,
        size: String(entry.size),
        modifiedAt: String(entry.modifiedAt),
        relativePath: relativePath ? `${relativePath}/${id}` : id,
      });
    };

  if (loading) {
    return (
      <StateWrap>
        <WavyLine size={16} opacity={0.4} />
      </StateWrap>
    );
  }

  if (error) {
    return (
      <StateWrap>
        <IconWrap>
          <Icon name="traffic-cone" size={16} />
        </IconWrap>
        <Text variant="text-regular-md">Couldn’t list this directory</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          {error}
        </Text>
      </StateWrap>
    );
  }

  return (
    <FileBrowserColumn
      heading={heading}
      items={items}
      selectedId={selectedId}
      focusedColumn={focusedColumn}
      focusedIndex={focusedIndex}
      columnIndex={columnIndex}
      onSelect={withEntryMeta(onSelect)}
      onReplace={onReplace ? withEntryReplaceMeta(onReplace) : undefined}
      onRegisterCount={onRegisterCount}
    />
  );
}
